import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BCreditRiskTrackerLegend from '../BusinessCreditScore/BCreditRiskTrackerLegend';
import BCreditRiskIndicator from './BCreditRiskIndicator';
import BCreditRiskTrackerDetails from './BCreditRiskTrackerDetails';
import BCreditNotRated from './BCreditNotRated';
import { ApplicationState } from '../../../store';
import Chart from "react-google-charts";
import { IBusinessCreditReportResponse, BusinessCreditReportStatePII } from '../../../store/businessCreditReportService/types';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import * as Service from '../../../store/service';
import * as BusinessCreditReportAction from '../../../store/businessCreditReportService/actions';
import RenderIcons from '../../../Assets/css/_icons/partner_icon/RenderIcons';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debug } from 'util';

interface IProps {
    reportObject: BusinessCreditReportStatePII;
    showDetails: boolean;
    view: string;
    isFromDailog?: boolean;
    profileStatus?: any;
}

const BCreditRiskTracker: React.FunctionComponent<IProps> = (props) => {
    const { t: tLang } = useTranslation();
    const [tableViewDiv, setTableViewDiv] = useState(true);

    const [showTracker, setShowTracker] = useState(true);
    const [isNotRated, setNotRated] = useState(false);
    const [isBankrupt, setIsBankrupt] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [graphLabel, setGraphLabel] = useState('The graph depicts how your Credit Risk Class Ranking has changed over time.');
    const [tooltipTriggerType, setTooltipTriggerType] = useState('visible');

    const dispatch = useDispatch();
    const requestBusinessCreditReport = (ReportPK: any, forcefullyRefresh: boolean, creditProvider: string) => dispatch(BusinessCreditReportAction.actionCreators.requestBusinessCreditReport(ReportPK, forcefullyRefresh, creditProvider));
    const businessProfileStatus = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userServiceDetails?.businessProfileStatus?.filter((obj: any) => { return obj.provider; }));
    const creditReportResponses: IBusinessCreditReportResponse[] = useSelector((state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.creditReportData)!);
    const isLoadingCreditReport: boolean = useSelector((state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.isLoading)!);
    const [tooltipTrigger, setTooltiptrigger] = useState(false);
    let options = {
        title: '',
        hAxis: {
            title: '',
            titleTextStyle: {
                color: '#333'
            }
        },
        pointSize: 5,
        legend: "none",
        vAxis: {
            baselineColor: '#fff',
            ticks: isBankrupt ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5],
            textPosition: 'left',
            viewWindowMode: 'pretty',
            viewWindow: {
                max: isBankrupt ? 6 : 5,
                min: 1,
            }
        },
        colors: ['#136be4'],
        height: 200,
        tooltip: {
            isHtml: true, 
            trigger: tooltipTrigger == false ? 'selection' : 'none'
        },
        chartArea: {
            left: 22,
            top: 10, // prevents top-most point on graph from getting cut off
            bottom: 26,
            right: 22,
            width: "70%",
            height: "100%"
        },
        series: { 
        }
    };

    const getToolTipHtml = (monthName: string, score: string) => {
        let html = '<div style="padding: 10px; height: 80px; width: 200px!important"><h5 style="color:black;">' + monthName + '</h5>' +
            '<span style="font-family:Arial;">VantageScore &reg; 3.0:</span><h5 style="color:black;">' + (score === '6' ? 'X' : score) + '</h5></div>';

        return html;
    }

    const ensureDataFetched = () => {
        const creditProvider = businessProfileStatus != null && businessProfileStatus.length > 0 ? businessProfileStatus[0].provider : '';
        const profileStatus = businessProfileStatus != null && businessProfileStatus.length > 0 ? businessProfileStatus[0].status : '';

        if (!props.isFromDailog) {
            requestBusinessCreditReport(null, false, creditProvider);
        }

        let dataForChart: any = [];

        if (chartData.length === 0) {
            const parser = new DOMParser();
            const strToDecode = 'VantageScore &reg; 3.0';
            const decodedString = parser.parseFromString(`<!doctype html><body>${strToDecode}`, 'text/html').body.textContent;

            dataForChart.push(['Month', decodedString
                , { role: "tooltip", type: "string", p: { html: true } }
            ]);
            if (!isLoadingCreditReport) {
                Service.getBusinessCreditTracker(creditProvider).then(data => {
                    data = JSON.parse(data);
                    if (data.HasError == false && data.Score) {
                        data.Score.sort(function (a: any, b: any) {
                            let aScoreTime = moment(a.CreatedDate, 'MM/D/YYYY').toDate().getTime();
                            let bScoreTime = moment(b.CreatedDate, 'MM/D/YYYY').toDate().getTime();
                            return aScoreTime - bScoreTime;
                        });
                        if (data.Score.length === 1) {
                            dataForChart.push([
                                '',
                                Number(data.Score[0].Value)
                                , getToolTipHtml('', data.Score[0].Value)
                            ]);
                            setTooltipTriggerType('none');
                        }
                        else { setTooltipTriggerType('visible'); }

                        data.Score.forEach((score: any) => {
                            if (score.Value === '0') {
                                setIsBankrupt(true);
                                setGraphLabel('The graph depicts how your Credit Risk Class Ranking has changed over time. An “X” ranking indicates open bankruptcy, out of business, or higher risk situations.');
                                score.Value = '6';
                            }
                            let month = moment(score.DateOfLatestRatingChange, 'YYYY-MM-DD').format('MMM DD, YYYY');
                            dataForChart.push([
                                month,
                                Number(score.Value)
                                , getToolTipHtml(month, score.Value)
                            ]);
                        });

                        setChartData(dataForChart);
                    } else {
                        if (data.Score === null) {
                            setNotRated(true);
                        }

                        if (profileStatus === 'NONE') {
                            setShowTracker(false);
                        } else {
                            setShowTracker(true);
                        }
                    }
                }).catch(function (error) {

                });
            }
        }
    }

    useEffect(ensureDataFetched, [creditReportResponses]);

    if (showTracker) {
        if (isNotRated) {
            return <BCreditNotRated reportObject={props.reportObject} showDetails={props.showDetails} view={props.view} />
        }
        return (
            <React.Fragment>
                {chartData.length > 1 ?

                    <div className={props.view === 'score' ? "box offset-padding" : props.view === 'dashboard' ? 'box' : ""}>
                        <div className="box-header">
                            {props.view === 'report' ?
                                <div className="row full-width between-xs margin-bottom-l payment-table-header">
                                    <div className="col-xs flex-grow-null"><h4 className="content-sub-title">Credit Risk Class</h4></div>
                                </div>
                                :
                                <React.Fragment>
                                    <div className="box-title-container credit-container">
                                        {props.view === 'score' &&
                                            <div className="box-title-icon" aria-hidden="true"><RenderIcons iconName='chart-line' /></div>
                                        }
                                        <div className="box-title-text">

                                            <h2 className="box-title" id="credit-score-trend-title">Credit Risk Class History</h2>
                                            <div>
                                                {(props.showDetails === false || props.view === 'dashboard') &&
                                                    <div className="box-actions"><Link to="/credit/score" className="default_link">Find out more</Link></div>
                                                }
                                                {tableViewDiv === true ? (
                                                    <div onClick={() => setTableViewDiv(false)} className="box-actions credit-score-top">
                                                        <NavLink to="#" className="default_link">
                                                            <span className="nav-text">{tLang('credit.creditScore.creditScoreTrackerWidget.label.tableScoreView')}</span>
                                                        </NavLink>
                                                    </div>
                                                ) : (
                                                    <div onClick={() => setTableViewDiv(true)} className="box-actions credit-score-top">
                                                        <NavLink to="#" className="default_link">
                                                            <span className="nav-text">{tLang('credit.creditScore.creditScoreTrackerWidget.label.cartScoreView')}</span>
                                                        </NavLink>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>



                                </React.Fragment>
                            }
                        </div>
                        <div className={props.view !== 'score' ? "flex-row graphics-container " + props.view : "box-content graphics-container"}>
                            {props.view !== 'score' &&
                                <BCreditRiskIndicator
                                    reportObject={props.reportObject}
                                    view={props.view}
                                    isBankrupt={isBankrupt}
                                />
                            }
                            <div className={"credit-score-trend-chart"}>
                                <div id="chart-credit-trend" className="m-chart-container">
                                    {tableViewDiv === true && (
                                        <BCreditRiskTrackerLegend reportObject={props.reportObject} isBankrupt={isBankrupt} />
                                    )}
                                    {tableViewDiv === false ? (
                                        <table className="svg-table">
                                            <tbody>
                                                {chartData.map((index, key) => (

                                                    (!!(index[0]) ?

                                                        <><tr key={key}>
                                                            <td className="google-visualization-table-tr-head">{index[0]}</td>
                                                            <td>{index[1]}</td>
                                                        </tr>
                                                        </> : console.log(''))

                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <Chart
                                            chartType="AreaChart"
                                            loader={<div>Loading Chart</div>}
                                            data={chartData}
                                            options={options}
                                            rootProps={{ 'data-testid': '2' }}
                                            chartEvents={[
                                                {
                                                    eventName: "ready",
                                                    callback: ({ chartWrapper, google }) => {
                                                        var chart = new google.visualization.AreaChart(document.getElementById('chart-credit-trend'));
                                                        var svg = chart.getContainer().getElementsByTagName('svg');
                                                        if (svg.length > 0) {
                                                            svg[0].setAttribute('role', 'img');
                                                            svg[0].setAttribute('aria-label', 'Credit Risk Tracker Bar Graph');
                                                        }
                                                        document.addEventListener('keydown', (event) => {
                                                            if (event.key === 'Escape') {
                                                                setTooltiptrigger(true);
                                                                event.stopPropagation();
                                                                setTooltiptrigger(false);
                                                            }
                                                        });
                                                        const datachart = document.getElementsByClassName('google-visualization-tooltip');
                                                        document.addEventListener('mousedown', (event) => {
                                                            if (datachart.length != 0) {
                                                                if (event instanceof MouseEvent) {
                                                                    setTooltiptrigger(true);
                                                                    setTooltiptrigger(false);
                                                                }
                                                            }
                                                        });
                                                    }
                                                }
                                            ]}
                                        />
                                    )}
                                    {props.view === 'report' &&
                                        <div className="graph-label">{graphLabel}</div>
                                    }
                                </div>
                            </div>

                            {props.view !== 'dashboard' && props.showDetails && chartData.length > 1 &&
                                <React.Fragment>
                                    <BCreditRiskTrackerDetails reportObject={props.reportObject} />
                                </React.Fragment>
                            }
                        </div>
                        {props.view === 'dashboard' && props.showDetails &&
                            <React.Fragment>
                                <BCreditRiskTrackerDetails reportObject={props.reportObject} />
                            </React.Fragment>
                        }
                    </div>
                : <div className="box loading"></div> }
            </React.Fragment>
        );
    }
    else { return null }
}

export default BCreditRiskTracker;
